import { KeyboardEvent, MouseEvent } from 'react';
import { Col, Dropdown, Form, Row, Stack } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import {
  getLanguage,
  getLocale,
  I18NextNS,
  LocaleCode,
} from 'src/models/Language.model';
import { ReportTemplate } from 'src/models/ReportTemplate.model';

import { DOB_INPUT_FORMATS } from 'src/utils/formatDate';
import {
  firstNameValidators,
  lastNameValidators,
} from 'src/utils/formValidators';
import {
  pidPattern,
  validateDatePattern,
  validateNameLength,
  validateNamePattern,
  validatePID,
  validatePIDLength,
} from 'src/utils/validationRules';

type FormValues = {
  dob: string;
  firstName: string;
  lastName: string;
  id: string;
  template: ReportTemplate;
  siteId: string;
  siteName: string;
};

type NewOrderCreationFormProps = {
  onSubmit: (data: FormValues) => void;
};

export default function NewOrderCreationForm({
  onSubmit,
}: NewOrderCreationFormProps) {
  const {
    register,
    control,
    setValue,
    clearErrors,
    setError,
    watch,
    handleSubmit,
    formState: { errors },
  } = useFormContext<FormValues>();
  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.COMMON);
  const { sites } = useRemoteBoundStore();

  const ValidFieldIcon = (field: keyof FormValues) => {
    return (
      (watch(field) && !errors[field] && (
        <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
      )) ||
      false
    );
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Form.Group controlId="dob">
          <Form.Label>
            {t('patientFields.DOB')} ({t('dateFormat')}){ValidFieldIcon('dob')}
          </Form.Label>

          <Controller
            control={control}
            name="dob"
            rules={{ required: t('patientFields.dobRequired') }}
            render={({ field: { onChange, value } }) => (
              <div className="position-relative" data-test="new-order-dob">
                <DatePicker
                  locale={getLocale(getLanguage(language as LocaleCode))}
                  id="dob"
                  autoComplete="off"
                  showIcon={false}
                  wrapperClassName="w-100"
                  className="form-control"
                  popperClassName="custom-datepicker-popper"
                  calendarClassName="custom-datepicker"
                  placeholderText={t('dateFormat')}
                  dateFormat={DOB_INPUT_FORMATS}
                  maxDate={new Date()}
                  showMonthDropdown
                  showYearDropdown
                  yearDropdownItemNumber={150}
                  scrollableYearDropdown
                  onChange={(
                    date: Date | null,
                    e?: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>,
                  ) => {
                    const target = e?.target as HTMLInputElement;
                    if (!target.value || validateDatePattern(target.value)) {
                      setValue('dob', moment(value).toDate().toDateString());
                      onChange(date);
                    }
                  }}
                  selected={value ? moment(value).toDate() : undefined}
                />
              </div>
            )}
          />
          {errors.dob?.message && (
            <p className="text-danger">{errors.dob?.message}</p>
          )}
        </Form.Group>

        <Row>
          <Form.Group as={Col} controlId="firstName">
            <Form.Label>
              {t('patientFields.First Name')}
              {ValidFieldIcon('firstName')}
            </Form.Label>

            <Form.Control
              placeholder={t('patientFields.firstNamePlaceholder')}
              data-test="new-order-first-name-input"
              {...register('firstName', firstNameValidators(t))}
              onChange={(e) => {
                if (e.target.value) {
                  if (!validateNamePattern(e.target.value)) {
                    setError('firstName', {
                      type: 'pattern',
                      message: t('patientFields.firstNameInvalid'),
                    });
                    return;
                  }

                  if (!validateNameLength(e.target.value)) {
                    setError('firstName', {
                      type: 'maxLength',
                      message: t('patientFields.firstNameMaxLength'),
                    });
                    return;
                  }
                }
                clearErrors('firstName');

                setValue('firstName', e.target.value);
              }}
              autoComplete="off"
            />
            {errors.firstName?.message && (
              <p className="text-danger">{errors.firstName?.message}</p>
            )}
          </Form.Group>

          <Form.Group as={Col} controlId="lastName">
            <Form.Label>
              {t('patientFields.Last Name')}
              {ValidFieldIcon('lastName')}
            </Form.Label>

            <Form.Control
              placeholder={t('patientFields.lastNamePlaceholder')}
              data-test="new-order-last-name-input"
              {...register('lastName', lastNameValidators(t))}
              onChange={(e) => {
                if (e.target.value) {
                  if (!validateNamePattern(e.target.value)) {
                    setError('lastName', {
                      type: 'pattern',
                      message: t('patientFields.lastNameInvalid'),
                    });
                    return;
                  }

                  if (!validateNameLength(e.target.value)) {
                    setError('lastName', {
                      type: 'maxLength',
                      message: t('patientFields.lastNameMaxLength'),
                    });
                    return;
                  }
                }
                clearErrors('lastName');

                setValue('lastName', e.target.value);
              }}
              autoComplete="off"
            />

            {errors.lastName?.message && (
              <p className="text-danger">{errors.lastName?.message}</p>
            )}
          </Form.Group>
        </Row>

        <Form.Group>
          <Form.Label>
            {t('patientFields.PID')}
            {ValidFieldIcon('id')}
          </Form.Label>
          <Form.Control
            type="text"
            placeholder={t('patientFields.pidPlaceholder')}
            autoComplete="off"
            data-test="new-order-pid-input"
            {...register('id', {
              required: t('patientFields.pidRequired'),
              maxLength: {
                value: 20,
                message: t('patientFields.pidMaxLength'),
              },
              pattern: {
                value: pidPattern,
                message: t('patientFields.pidInvalid'),
              },
            })}
            onChange={(e) => {
              if (e.target.value) {
                if (!validatePIDLength(e.target.value)) {
                  setError('id', {
                    type: 'maxLength',
                    message: t('patientFields.pidMaxLength'),
                  });
                  return;
                }

                if (!validatePID(e.target.value)) {
                  setError('id', {
                    type: 'pattern',
                    message: t('patientFields.pidInvalid'),
                  });
                  return;
                }
              }
              clearErrors('id');

              setValue('id', e.target.value);
            }}
          />
          {errors.id?.message && (
            <p className="text-danger">{errors.id?.message}</p>
          )}
        </Form.Group>

        <Form.Group>
          <Form.Label>
            {t('patientFields.site')}
            {ValidFieldIcon('siteId')}
          </Form.Label>
          <Dropdown className="w-100" data-test="new-order-site-dropdown">
            <Dropdown.Toggle
              variant="outline-primary"
              className={`w-100 text-start ${errors.siteId ? 'border-danger' : ''}`}
              data-test="new-order-site-toggle"
            >
              {watch('siteName')
                ? watch('siteName')
                : t('patientFields.selectSite')}
            </Dropdown.Toggle>
            <Dropdown.Menu
              className="w-100 overflow-auto"
              style={{ maxHeight: '48vh' }}
              data-test="new-order-site-menu"
            >
              <>
                {sites.map((site) => (
                  <Dropdown.Item
                    key={site.siteId}
                    data-test="new-order-site-option"
                    onClick={() => {
                      setValue('siteId', site.siteId);
                      setValue('siteName', site.siteName);
                      clearErrors('siteId');
                    }}
                  >
                    {site.siteName}
                  </Dropdown.Item>
                ))}
              </>
            </Dropdown.Menu>
          </Dropdown>
          <input
            type="hidden"
            {...register('siteId', {
              required: t('patientFields.siteRequired'),
            })}
          />
          {errors.siteId?.message && (
            <p className="text-danger">{errors.siteId?.message}</p>
          )}
        </Form.Group>
      </Stack>
    </Form>
  );
}
